import React, { memo, useEffect, useRef, PropsWithChildren } from 'react';
import { autoScrollForElements } from '@atlaskit/pragmatic-drag-and-drop-auto-scroll/element';

import { ScaleFunction } from '@components/operatorPanel/interfaces';
import { font as initialFont } from '@styles/fonts';

import { useBoardContext } from './context';
import { scale } from '@components/frame';
import ColorWrapper from '@styles/colors';
import { ColorFunction } from '@styles/colors/types';
import { PLANBOARD_TILE_CONFIG } from '@v2/pages/common/constants/base.constants';
import { TourTarget } from '@components/tours';

interface ScollableBoardProps extends PropsWithChildren {
  scale?: ScaleFunction;
  font?: typeof initialFont;
  C: ColorFunction;
}

const ScrollableBoard: React.FC<ScollableBoardProps> = ({ children, C }) => {
  const { instanceId, setIsScrolled } = useBoardContext();

  const scrollableRef = useRef<HTMLDivElement>(null);

  const columnWidth = scale(PLANBOARD_TILE_CONFIG.COLUMN_CONTENT_WIDTH + PLANBOARD_TILE_CONFIG.COLUMN_PADDING_HORIZONTAL * 2);
  const columnWidthPx = `${columnWidth}px`;
  const boardWidth = `calc(100dvw - ${scale(PLANBOARD_TILE_CONFIG.COLUMN_PADDING_HORIZONTAL) * 2}px)`;

  useEffect(() => {
    if (!scrollableRef.current) return;

    return autoScrollForElements({ element: scrollableRef.current, canScroll: ({ source }) => source.data.instanceId === instanceId });
  }, [instanceId]);

  // Track horizontal scroll for sticky column shadow
  useEffect(() => {
    const element = scrollableRef.current;
    if (!element) return;

    const handleScroll = () => {
      setIsScrolled(element.scrollLeft > 0);
    };

    element.addEventListener('scroll', handleScroll);
    return () => element.removeEventListener('scroll', handleScroll);
  }, [setIsScrolled]);

  const isLight = C.scheme === 'light';
  return (
    <TourTarget tourId="planboard-columns-overview">
      <div
        style={{
          backgroundColor: C(`Background/${isLight ? 'Light' : 'Dark'}/Field [mobile]`, false),
          borderTop: `0.5px solid ${C(`Hairline/${isLight ? 'Light' : 'Dark'}/No trans [mobile]`, false)}`,
          minHeight: '100%',
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: columnWidthPx,
            gridAutoColumns: columnWidthPx,
            gridAutoFlow: 'column',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            gap: scale(6),
            flexDirection: 'row',
            overflow: 'scroll',
            height: '100%',
            width: boardWidth,
            backgroundColor: C(`Label/Light/Inverted`),
            maxHeight: 'calc(100dvh - 124px)',
          }}
          ref={scrollableRef}
        >
          {children}
        </div>
      </div>
    </TourTarget>
  );
};

export default memo(ColorWrapper(ScrollableBoard));
